import { Grid } from '~/engine/data/common/Grid';
import type { RoiId } from '~/domain/changes/ids';
import type { ContourStepMove } from '~/domain/undo/ContourEditStep';
import { DicomColor } from '../dicom/dicomColor';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { BaseDomainEvent } from './DomainEvent';
import { EventSource } from './EventTypes';

export type RoiPropertyUpdates = {
    name?: string;
    color?: DicomColor;
    visible?: boolean;
    locked?: boolean;
    approvalStatus?: DICOMApprovalStatus;
    contourThickness?: number;
    interpretedType?: RoiInterpretedType;
    electronDensityOverride?: number | undefined;
    massDensityOverride?: number | undefined;
    roiDescription?: string;
    roiGenerationAlgorithm?: RoiGenerationAlgorithmType;
    deleted?: boolean;
};

/**
 * Domain event emitted when {@link Roi} creation is requested
 */
export class RoiCreationRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CREATION_REQUESTED';

    constructor(
        structureSetUid: string,
        source: EventSource = 'user',
        interpretedType?: RoiInterpretedType,
        initialCoordinatesMm?: { x: number; y: number; z: number }
    ) {
        super(
            RoiCreationRequestedEvent.EVENT_TYPE,
            `roi-creation-for-${structureSetUid}`,
            {
                structureSetUid,
                source,
                interpretedType,
                initialCoordinatesMm,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get interpretedType(): RoiInterpretedType | undefined {
        return this.data.interpretedType;
    }

    get initialCoordinatesMm():
        | { x: number; y: number; z: number }
        | undefined {
        return this.data.initialCoordinatesMm;
    }
}

/**
 * Domain event emitted when {@link Roi} selection is requested
 */
export class RoiSelectionRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_SELECTION_REQUESTED';

    constructor(roiId: RoiId, source: EventSource = 'user') {
        super(RoiSelectionRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} deletion is requested
 */
export class RoiDeletionRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_DELETION_REQUESTED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        source: EventSource = 'user'
    ) {
        super(RoiDeletionRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            source,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} algorithm change is requested
 */
export class RoiAlgorithmChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_ALGORITHM_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        currentAlgorithm: RoiGenerationAlgorithmType,
        requestedAlgorithm: RoiGenerationAlgorithmType,
        reason: string
    ) {
        super(RoiAlgorithmChangeRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            currentAlgorithm,
            requestedAlgorithm,
            reason,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get currentAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.currentAlgorithm;
    }

    get requestedAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.requestedAlgorithm;
    }

    get reason(): string {
        return this.data.reason;
    }
}

/**
 * Domain event emitted when {@link Roi} visibility change is requested
 */
export class RoiVisibilityChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_VISIBILITY_CHANGE_REQUESTED';

    constructor(roiId: RoiId, visible: boolean, source: EventSource = 'user') {
        super(RoiVisibilityChangeRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            visible,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get visible(): boolean {
        return this.data.visible;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a {@link Roi} edit-lock change is requested.
 */
export class RoiLockChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_LOCK_CHANGE_REQUESTED';

    constructor(roiId: RoiId, locked: boolean, source: EventSource = 'user') {
        super(RoiLockChangeRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            locked,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get locked(): boolean {
        return this.data.locked;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} color change is requested
 */
export class RoiColorChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_COLOR_CHANGE_REQUESTED';

    constructor(roiId: RoiId, color: DicomColor, source: EventSource = 'user') {
        super(RoiColorChangeRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            color,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get color(): DicomColor {
        return this.data.color;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} contour thickness change is requested
 */
export class RoiContourThicknessChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOUR_THICKNESS_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        contourThickness: number,
        source: EventSource = 'user'
    ) {
        super(
            RoiContourThicknessChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, contourThickness, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get contourThickness(): number {
        return this.data.contourThickness;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} interpreted type change is requested
 */
export class RoiInterpretedTypeChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_INTERPRETED_TYPE_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        interpretedType: RoiInterpretedType,
        source: EventSource = 'user'
    ) {
        super(
            RoiInterpretedTypeChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, interpretedType, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get interpretedType(): RoiInterpretedType {
        return this.data.interpretedType;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when an {@link Roi} electron density override change
 * is requested.
 */
export class RoiElectronDensityOverrideChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE =
        'ROI_ELECTRON_DENSITY_OVERRIDE_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        electronDensityOverride: number | undefined,
        source: EventSource = 'user'
    ) {
        super(
            RoiElectronDensityOverrideChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, electronDensityOverride, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get electronDensityOverride(): number | undefined {
        return this.data.electronDensityOverride;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when an {@link Roi} mass density override change is
 * requested.
 */
export class RoiMassDensityOverrideChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_MASS_DENSITY_OVERRIDE_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        massDensityOverride: number | undefined,
        source: EventSource = 'user'
    ) {
        super(
            RoiMassDensityOverrideChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, massDensityOverride, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get massDensityOverride(): number | undefined {
        return this.data.massDensityOverride;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} description change is requested
 */
export class RoiDescriptionChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_DESCRIPTION_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        roiDescription: string,
        source: EventSource = 'user'
    ) {
        super(RoiDescriptionChangeRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            roiDescription,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get roiDescription(): string {
        return this.data.roiDescription;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} generation algorithm change is requested
 */
export class RoiGenerationAlgorithmChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_GENERATION_ALGORITHM_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        roiGenerationAlgorithm: RoiGenerationAlgorithmType,
        source: EventSource = 'user'
    ) {
        super(
            RoiGenerationAlgorithmChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, roiGenerationAlgorithm, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get roiGenerationAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.roiGenerationAlgorithm;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} contour modification is started
 */
export class RoiContourModificationStartedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOUR_MODIFICATION_STARTED';

    constructor(
        roiId: RoiId,
        operation: string,
        pixels: Uint8Array[],
        grid: Grid,
        source: EventSource = 'user'
    ) {
        super(RoiContourModificationStartedEvent.EVENT_TYPE, `roi-${roiId}`, {
            roiId,
            operation,
            pixels,
            grid,
            source,
        });
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get operation(): string {
        return this.data.operation;
    }

    get pixels(): Uint8Array[] {
        return this.data.pixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * `modificationType` value of the body-contour generation write
 * (SR-WOR-H-27-1). Writes carrying this label AUTHOR the ROI's contours
 * (threshold extraction) rather than editing them, so the
 * AUTOMATIC → SEMI_AUTOMATIC demotion on contour edits must not apply —
 * the same operation declares AUTOMATIC as part of the apply.
 */
export const BODY_CONTOUR_OPERATION = 'Body contour';

/**
 * Domain event emitted when {@link Roi} contours are modified
 */
export class RoiContoursModifiedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOURS_MODIFIED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        modificationType: string,
        pixels: Uint8Array[],
        grid: Grid,
        initialPixels?: Uint8Array[],
        initialGrid?: Grid
    ) {
        super(RoiContoursModifiedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            modificationType,
            pixels,
            grid,
            initialPixels,
            initialGrid,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get modificationType(): string {
        return this.data.modificationType;
    }

    get pixels(): Uint8Array[] {
        return this.data.pixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get initialPixels(): Uint8Array[] | undefined {
        return this.data.initialPixels;
    }

    get initialGrid(): Grid | undefined {
        return this.data.initialGrid;
    }
}

/**
 * Domain event emitted when {@link Roi} contour undo is requested
 */
export class ContourUndoRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'CONTOUR_UNDO_REQUESTED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        compressedPixels: Uint8Array,
        grid: Grid,
        source: EventSource = 'user',
        /** The history steps the restore left and restored, when the publisher knows them. */
        steps?: ContourStepMove
    ) {
        super(ContourUndoRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            compressedPixels,
            grid,
            source,
            steps,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get compressedPixels(): Uint8Array {
        return this.data.compressedPixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get steps(): ContourStepMove | undefined {
        return this.data.steps;
    }
}

/**
 * Domain event emitted when {@link Roi} contour redo is requested
 */
export class ContourRedoRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'CONTOUR_REDO_REQUESTED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        compressedPixels: Uint8Array,
        grid: Grid,
        source: EventSource = 'user',
        /** The history steps the restore left and restored, when the publisher knows them. */
        steps?: ContourStepMove
    ) {
        super(ContourRedoRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            compressedPixels,
            grid,
            source,
            steps,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get compressedPixels(): Uint8Array {
        return this.data.compressedPixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get steps(): ContourStepMove | undefined {
        return this.data.steps;
    }
}

/**
 * The domain events that mutate a single ROI's contour SDF in place. A brush /
 * edit stroke emits a {@link RoiContoursModifiedEvent}, while undo and redo
 * restore the pixels through {@link ContourUndoRequestedEvent} /
 * {@link ContourRedoRequestedEvent} WITHOUT re-emitting a modified event. All
 * three carry a `roiId`, so any overlay, preview, or read-out keyed on a
 * ROI's live texture must observe all three or it will go stale after an edit
 * or an undo/redo.
 */
export type RoiContourEditEvent =
    | RoiContoursModifiedEvent
    | ContourUndoRequestedEvent
    | ContourRedoRequestedEvent;

/**
 * The event types of every {@link RoiContourEditEvent}: subscribe to all of
 * them to observe each change to a ROI's contour SDF, including undo and redo.
 */
export const ROI_CONTOUR_EDIT_EVENT_TYPES: readonly string[] = [
    RoiContoursModifiedEvent.EVENT_TYPE,
    ContourUndoRequestedEvent.EVENT_TYPE,
    ContourRedoRequestedEvent.EVENT_TYPE,
];

/**
 * Domain event emitted when {@link Roi} properties are updated
 */
export class RoiPropertiesUpdatedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_PROPERTIES_UPDATED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        updatedProperties: RoiPropertyUpdates,
        updateSource: EventSource
    ) {
        super(RoiPropertiesUpdatedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            updatedProperties,
            updateSource,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get updatedProperties(): RoiPropertyUpdates {
        return this.data.updatedProperties;
    }

    get updateSource(): EventSource {
        return this.data.updateSource;
    }
}

/**
 * Domain event emitted when {@link Roi} is created
 */
export class RoiCreatedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CREATED';

    constructor(
        structureSetUid: string,
        roiId: RoiId,
        source: EventSource = 'system'
    ) {
        super(RoiCreatedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            source,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} point coordinates change is requested.
 * Used for repositioning POINT-type ROIs (markers, isocenters, registrations).
 * Coordinates are in patient space (mm).
 */
export class RoiPointCoordinatesChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_POINT_COORDINATES_CHANGE_REQUESTED';

    constructor(
        roiId: RoiId,
        coordinates: { x: number; y: number; z: number },
        source: EventSource = 'user'
    ) {
        super(
            RoiPointCoordinatesChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiId}`,
            { roiId, coordinates, source }
        );
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }
    get coordinates(): { x: number; y: number; z: number } {
        return this.data.coordinates;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} copy is requested
 */
export class RoiCopyRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_COPY_REQUESTED';

    constructor(structureSetUid: string, roiId: RoiId, source: EventSource) {
        super(RoiCopyRequestedEvent.EVENT_TYPE, `roi-${roiId}`, {
            structureSetUid,
            roiId,
            source,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiId(): RoiId {
        return this.data.roiId;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when SDF copy-over from one {@link Roi}
 * to another is requested.
 */
export class RoiSdfCopyOverRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_SDF_COPY_OVER_REQUESTED';

    constructor(
        structureSetUid: string,
        sourceRoiId: RoiId,
        targetRoiId: RoiId,
        source: EventSource
    ) {
        super(
            RoiSdfCopyOverRequestedEvent.EVENT_TYPE,
            `roi-sdf-copy-${sourceRoiId}-to-${targetRoiId}`,
            {
                structureSetUid,
                sourceRoiId,
                targetRoiId,
                source,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get sourceRoiId(): RoiId {
        return this.data.sourceRoiId;
    }

    get targetRoiId(): RoiId {
        return this.data.targetRoiId;
    }

    get source(): EventSource {
        return this.data.source;
    }
}
